import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Dialog } from "radix-ui";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useCreateTeam, useResources, useSyncNow, useTeams } from "../../../api/hooks";
import { TeamBadge, TeamEditor } from "../../../components/admin/TeamEditor";
import { PageHead } from "../../../components/shell/Page";
import { LoadFailed } from "../../../components/ui/LoadState";
import { Icon } from "../../../components/ui/primitives";
import { t, tn } from "../../../i18n";
import { useUi } from "../../../state/ui";

type Search = { id?: string };

export const Route = createFileRoute("/_app/admin/teams")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    id: typeof s.id === "string" ? s.id : undefined,
  }),
  component: TeamsPage,
});

function NewTeamDialog({ onCreated }: { onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTeam();
  const toast = useUi((s) => s.toast);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const team = await create.mutateAsync({ name, about, listen_enabled: true });
      toast(t("admin.createdTeam", { team: team.name }));
      setOpen(false);
      setName("");
      setAbout("");
      onCreated(team.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("admin.couldntCreateTeam"));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {/* R7.5: secondary (the screen's one orange action is the editor's Save), and still
            there on a phone. */}
        <button type="button" className="btn btn-secondary keep-xs">
          <Icon name="i-plus" size="s" />
          {t("admin.newTeam")}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog" aria-describedby={undefined}>
          <form onSubmit={submit}>
            <div className="dialog-head">
              <Dialog.Title asChild>
                <h2>{t("admin.newTeam")}</h2>
              </Dialog.Title>
            </div>
            <div className="dialog-body frm">
              <div className="fld">
                <label htmlFor="team-name">{t("admin.name")}</label>
                <input
                  id="team-name"
                  className="inp"
                  required
                  maxLength={60}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="fld">
                <label htmlFor="team-about">{t("admin.aboutTeam")}</label>
                <textarea
                  id="team-about"
                  className="inp"
                  rows={2}
                  maxLength={500}
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                />
                <div className="help">{t("admin.newTeamsHint")}</div>
              </div>
              {error && <div className="form-error">{error}</div>}
            </div>
            <div className="dialog-foot">
              <Dialog.Close asChild>
                <button type="button" className="btn btn-ghost">
                  {t("common.cancel")}
                </button>
              </Dialog.Close>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!name.trim() || create.isPending}
              >
                {t("admin.createTeam")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const TOOL_NAMES: Record<string, string> = {
  redmine: "Redmine",
  github: "GitHub",
  outline: "Outline",
};

function SyncButton() {
  const sync = useSyncNow();
  const toast = useUi((s) => s.toast);
  return (
    <button
      type="button"
      className="btn btn-secondary"
      disabled={sync.isPending}
      onClick={() =>
        sync.mutate(undefined, {
          onSuccess: (r) => {
            const parts = Object.entries(r.tools).map(([tool, res]) =>
              res.error
                ? t("admin.syncToolFailed", { tool: TOOL_NAMES[tool] ?? tool })
                : `${TOOL_NAMES[tool]} ${res.seen}`,
            );
            const flagged = r.flagged_total
              ? ` · ${t("admin.withoutAccess", { n: r.flagged_total })}`
              : "";
            toast(
              `${t("admin.synced", { list: parts.join(", ") })}${flagged}`,
              Object.values(r.tools).some((t) => t.error),
            );
          },
          onError: () => toast(t("admin.syncFailed"), true),
        })
      }
    >
      <Icon name="i-refresh" size="s" />
      {sync.isPending ? t("admin.syncing") : t("admin.syncNow")}
    </button>
  );
}

/** New items nobody can reach yet (PLAN §8.1 "Ungranted flag"). */
function FlaggedBanner() {
  const resources = useResources();
  const flagged = (resources.data ?? []).filter((r) => r.flagged_ungranted_at && !r.archived);
  if (!flagged.length) return null;
  const names = flagged
    .slice(0, 4)
    .map((r) => `${r.name} (${TOOL_NAMES[r.tool]})`)
    .join(", ");
  return (
    <div className="flag-banner" role="status">
      <Icon name="i-alert" size="s" />
      <span>
        <b>{tn("admin.noAccessYet", flagged.length)}</b> {names}
        {flagged.length > 4 ? ` ${t("admin.andMore", { n: flagged.length - 4 })}` : ""}.{" "}
        {t("admin.grantBelow")}
      </span>
    </div>
  );
}

function TeamsPage() {
  const { id } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // R9-A5: "Show archived" lists the archived teams too, marked; an archived team asked for by
  // id (just archived, or a link) keeps its editor even while they're hidden.
  const [showArchived, setShowArchived] = useState(false);
  const teams = useTeams(showArchived);
  const list = teams.data ?? [];
  const current = list.find((t) => t.id === id) ?? list.find((t) => t.slug === "dev") ?? list[0];
  const editorId = id ?? current?.id;
  const select = (tid: string) => navigate({ search: { id: tid } });
  // The team strip (a row at iPad width and on phones) shows the team being edited (R10-T6).
  const strip = useRef<HTMLElement>(null);
  useEffect(() => {
    strip.current
      ?.querySelector<HTMLElement>('[aria-current="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [editorId, list.length]);

  return (
    <main className="sub main on no-panel" aria-label={t("shell.teams")}>
      <div className="page">
        <PageHead
          title={t("shell.teams")}
          subtitle={t("admin.teamsSubtitle")}
          actions={
            <>
              {/* The label says what pressing does (R10-K13). */}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowArchived((v) => !v)}
              >
                {showArchived ? t("admin.hideArchived") : t("admin.showArchived")}
              </button>
              <SyncButton />
              <NewTeamDialog onCreated={select} />
            </>
          }
        />
        <FlaggedBanner />
        <div className="teams-wrap">
          <nav ref={strip} className="team-list scroll" aria-label={t("shell.teams")}>
            {teams.isLoading && (
              <div className="load-state" role="status" aria-busy="true">
                {t("common.loadingWhat", { what: t("admin.whatTeams") })}
              </div>
            )}
            {teams.isError && <LoadFailed query={teams} what={t("admin.whatTheTeams")} />}
            {list.map((tm) => (
              <button
                key={tm.id}
                type="button"
                className="team-item"
                aria-current={tm.id === editorId ? "true" : undefined}
                onClick={() => select(tm.id)}
              >
                <TeamBadge team={tm} />
                <span>
                  <span className="ti-name">
                    {tm.name}
                    {tm.archived && (
                      <span className="tag-ai ti-archived">{t("admin.archived")}</span>
                    )}
                  </span>
                  <span className="ti-sub">
                    {tm.kind === "everyone"
                      ? t("admin.wholeCompany", { n: tm.member_count })
                      : `${tn("admin.peopleCount", tm.member_count)} · #${tm.slug}`}
                  </span>
                </span>
              </button>
            ))}
          </nav>
          {editorId ? (
            <TeamEditor key={editorId} teamId={editorId} />
          ) : (
            <div className="team-editor" />
          )}
        </div>
      </div>
    </main>
  );
}
